import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '../utils/toast';
import { X, Star, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackModal = ({ isOpen, onClose, trainingTopicId, topicName, feedbackSet, onSubmit, isMandatory = false }) => {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleRatingChange = (questionId, rating) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { question_id: questionId, question_type: 'rating', answer: rating || 0 }
    }));
  };

  const handleTextChange = (questionId, text) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { question_id: questionId, question_type: 'text', answer: text || '' }
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      console.log('[Feedback Submit] trainingTopicId received:', trainingTopicId);
      console.log('[Feedback Submit] trainingTopicId type:', typeof trainingTopicId);
      
      // Validate trainingTopicId
      if (!trainingTopicId || typeof trainingTopicId !== 'string' || trainingTopicId.trim() === '' || trainingTopicId === 'unknown') {
        console.error('[Feedback Submit] Invalid trainingTopicId:', trainingTopicId);
        console.error('[Feedback Submit] feedbackSet:', feedbackSet);
        toast.error('Invalid topic ID. Please try reopening the training.');
        setSubmitting(false);
        return;
      }
      
      // Filter and validate answers to ensure all have the 'answer' field with valid values
      const answersList = Object.values(answers).filter(ans => {
        if (!ans || ans.answer === undefined || ans.answer === null) return false;
        if (ans.question_type === 'rating' && (!ans.answer || ans.answer <= 0)) return false;
        if (ans.question_type === 'text' && typeof ans.answer === 'string' && ans.answer.trim() === '') return false;
        return true;
      });
      
      const finalTopicId = String(trainingTopicId).trim();
      
      console.log('[Feedback Submit] Submitting feedback for topic:', finalTopicId);
      
      const response = await axios.post(`${API}/feedback/submit`, {
        training_topic_id: finalTopicId,
        feedback_set_id: feedbackSet.feedback_set_id,
        answers: answersList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Feedback Submit] Success:', response.data);
      toast.success('Thank you for your feedback!');
      onSubmit();
      onClose();
    } catch (error) {
      console.error('[Feedback Submit] Error:', error.response?.data || error.message);
      
      // Get detailed error message
      let errorMsg = 'Unknown error';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMsg = error.response.data.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        } else {
          errorMsg = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(`Failed to submit feedback: ${errorMsg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (isMandatory) {
      // For mandatory feedback, don't allow skip
      toast.warning('⚠️ This feedback is mandatory. Please complete it to proceed.');
      return;
    } else {
      onClose();
    }
  };

  const handleCloseAttempt = (open) => {
    if (!open && isMandatory) {
      // User trying to close mandatory feedback without submitting
      // Do nothing - mandatory feedback cannot be closed without submission
      toast.warning('⚠️ Please complete the feedback to proceed.');
      return;
    } else if (!open) {
      onClose();
    }
  };

  if (!feedbackSet) return null;

  const allQuestionsAnswered = feedbackSet.questions?.every(q => {
    // Skip validation for optional questions
    if (q.is_required === false) {
      return true; // Optional questions don't block submission
    }
    
    const answer = answers[q.question_id];
    if (!answer) return false;
    
    // Check that answer field exists and has a value
    if (answer.question_type === 'rating') {
      return answer.answer && answer.answer > 0;
    } else {
      return answer.answer !== undefined && answer.answer !== null && answer.answer.trim() !== '';
    }
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseAttempt}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-hidden p-5" hideClose={isMandatory}>
        {/* Header */}
        <div className="pb-3 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">📝 Training Feedback {isMandatory && <span className="text-red-600">*</span>}</h2>
          {topicName && (
            <p className="text-sm text-blue-700 font-medium mt-1">{topicName}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">{feedbackSet.description || 'Please share your feedback on this training to help us improve'}</p>
        </div>

        {/* Questions */}
        <div className="flex-1 overflow-y-auto min-h-0 mt-4">
        <div className="space-y-3 mb-4">
          {feedbackSet.questions?.map((question, index) => (
            <div key={question.question_id} className="bg-gray-50 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  {index + 1}
                </span>
                <h3 className="text-sm font-medium text-gray-900 leading-snug">{question.question_text}</h3>
                {question.is_required === false && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 bg-orange-100 rounded flex-shrink-0">
                    Optional
                  </span>
                )}
              </div>

              {question.question_type === 'rating' ? (
                <div className="flex items-center space-x-1.5 pl-7">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => handleRatingChange(question.question_id, rating)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-7 h-7 ${
                          answers[question.question_id]?.answer >= rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                  {answers[question.question_id]?.answer && (
                    <span className="ml-2 text-xs font-medium text-gray-500">
                      {answers[question.question_id].answer}/5
                    </span>
                  )}
                </div>
              ) : (
                <textarea
                  placeholder="Type your feedback here..."
                  value={answers[question.question_id]?.answer || ''}
                  onChange={(e) => handleTextChange(question.question_id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                  rows={3}
                />
              )}
            </div>
          ))}
        </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200 flex-shrink-0">
          {!isMandatory && (
            <Button
              onClick={handleSkip}
              variant="outline"
              className="text-gray-600"
            >
              Skip for Now
            </Button>
          )}
          {isMandatory && (
            <p className="text-sm text-orange-600 font-medium">
              ⚠️ Feedback is mandatory.
            </p>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!allQuestionsAnswered || submitting}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400"
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
